import { useEffect, useState } from 'react';
import './ProjectsContent.css';
import { getProjects } from '../../utilities/api/projectsTimeline';
import ProjectsFilter from '../../components/ProjectsFilter/ProjectsFilter';
import ProjectsItem from '../../components/ProjectsItem/ProjectsItem';
import { getAllUsersBasic } from '../../utilities/api/users';
import UpdateProject from '../../components/UpdateProject/UpdateProject';
import NewProject from '../../components/NewProject/NewProject';
import { dateSort, projNumSort } from '../../utilities/services/sortConditionally';
import SearchBar from '../../components/SearchBar/SearchBar';

export default function ProjectsContent({ user, toggleNotifications, isMobile, projectPreferences, services }) {
  const [loading, setLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [users, setUsers] = useState([])
  const [sortOrder, setSortOrder] = useState('desc')
  const [sortHeader, setSortHeader] = useState('clientNumber')

  const getProjectsTimeline = async () => {
    setLoading(true)
    try {
      const allProjects = await getProjects(user)
      // convert the client number to an integer for sorting
      allProjects.forEach(project => {
        project.clientNumber = project.clientNumber ? parseInt(project.clientNumber) : ''
      })
      setProjects(allProjects.sort((a, b) => a[sortHeader] < b[sortHeader] ? 1 : -1))
    } catch (err) {
      console.log(err)
    }
    setLoading(false)
  }

  const getUsers = async () => {
    try {
      const data = await getAllUsersBasic()
      // console.log(data)
      setUsers(data)
    } catch (error) {
      console.log(error)
    }
  }

  const updateProjects = (updatedProjects) => {
    updatedProjects.forEach(project => {
        project.clientNumber = project.clientNumber ? parseInt(project.clientNumber) : ''
    });

    let sortedProjects = [...updatedProjects];
    if (sortHeader === 'projNum') {
        sortedProjects = projNumSort(sortOrder, sortHeader, sortedProjects);
    } else if (['submissionDate', 'dueDate', 'clientDeadline'].includes(sortHeader)) {
        sortedProjects = dateSort(sortOrder, sortHeader, sortedProjects);
    } else {
        sortedProjects.sort((a, b) => {
            if (sortOrder === 'asc') {
                return ((typeof(a[sortHeader])==='string' ? a[sortHeader].toLowerCase() : a[sortHeader]) > (typeof(b[sortHeader])==='string' ? b[sortHeader].toLowerCase() : b[sortHeader])) ? 1 : -1;
            } else {
                return ((typeof(a[sortHeader])==='string' ? a[sortHeader].toLowerCase() : a[sortHeader]) < (typeof(b[sortHeader])==='string' ? b[sortHeader].toLowerCase() : b[sortHeader])) ? 1 : -1
            }
        });
    }
    setProjects(sortedProjects);
  };

  const handleTableHeaderClick = (evt) => {
    const header = evt.target.id;
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSortHeader(header);
    setSortOrder(newSortOrder);

    let sortedProjects = [...projects];
    if (header === 'projNum') {
        sortedProjects = projNumSort(newSortOrder, header, sortedProjects);
    } else if (['submissionDate', 'dueDate', 'clientDeadline'].includes(header)) {
        sortedProjects = dateSort(newSortOrder, header, sortedProjects);
    } else {
        sortedProjects.sort((a, b) => {
          if (newSortOrder === 'asc') {
            return ((typeof(a[header])==='string' ? a[header].toLowerCase() : a[header]) > (typeof(b[header])==='string' ? b[header].toLowerCase() : b[header])) ? 1 : -1;
          } else {
            return ((typeof(a[header])==='string' ? a[header].toLowerCase() : a[header]) < (typeof(b[header])==='string' ? b[header].toLowerCase() : b[header])) ? 1 : -1;
          }
        });
    }
    setProjects(sortedProjects);
  };

  useEffect(() => {
    getProjectsTimeline()
    getUsers()

    // eslint-disable-next-line
  }, [])

  return (
    <div className='ProjectsContent'>
      <div className='projectsHeadingContainer'>
        <div className='section1'>
          <div className='title'>
            Projects
          </div>
          <SearchBar user={user} section={'projects'} updateContent={updateProjects} />
        </div>
        { ['admin', 'manager'].includes(user.userType) &&
        <div className='section2'>
          <button className='addProjectButton' data-bs-toggle="modal" data-bs-target="#newProjectModal">
            <svg width="20" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="38" height="38" rx="10" fill="#FFFFFF" opacity='0.05'/>
                <path d="M19 11V27V11ZM27 19H11H27Z" fill="#2173F0"/>
                <path d="M19 11V27M27 19H11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>&nbsp;&nbsp;Add Project</span>
          </button>
          <NewProject user={user} users={users} updateProjects={updateProjects} services={services} toggleNotifications={toggleNotifications} />
        </div>
        }
      </div>
      { loading ?
      <div className='loading'>
        <div className="spinner-border" role="status"></div>
        <div><strong>Fetching most recent data</strong>...</div>
      </div>
      :
      <div className='projectsMainSection'>
        <div className='header'>
          <div className='title'>
            Project List
          </div>
          <div className='filterContainer'>
            <ProjectsFilter user={user} users={users} updateProjects={updateProjects} services={services} isMobile={isMobile} sortHeader={sortHeader} />
          </div>
        </div>
        <div className='projectsTable'>
          <table className=''>
            { projectPreferences &&
            <>
            <thead>
              <tr>
                { !projectPreferences.includes('projNum') &&
                <th id='projNum' onClick={handleTableHeaderClick} className='projNum' title='Click to sort by project number'>
                  <div className='tableHeaderWithArrow'><div>Project Number&nbsp;&nbsp;</div>
                  { sortHeader==='projNum'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('projType') &&
                <th id='projType' onClick={handleTableHeaderClick} className='projType' title='Click to sort by service'>
                  <div className='tableHeaderWithArrow'><div>Service&nbsp;&nbsp;</div>
                  { sortHeader==='projType'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('projTitle') &&
                <th id='projTitle' className='projTitle'>
                  <div className='tableHeaderWithArrow'>Title</div>
                </th>
                }
                { !projectPreferences.includes('clientNumber') &&
                <th id='clientNumber' onClick={handleTableHeaderClick} title='Click to sort by client number'>
                  <div className='tableHeaderWithArrow'><div>Client Number&nbsp;&nbsp;</div>
                  { sortHeader==='clientNumber'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('clientName') &&
                <th id='clientName' onClick={handleTableHeaderClick} title='Click to sort by client name'>
                  <div className='tableHeaderWithArrow'><div>Client Name&nbsp;&nbsp;</div>
                  { sortHeader==='clientName'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('submissionDate') &&
                <th id='submissionDate'>
                  <div className='submissionDate'>Submission Date</div>
                </th>
                }
                { !projectPreferences.includes('dueDate') &&
                <th id='dueDate' onClick={handleTableHeaderClick} className='dueDate' title='Click to sort by due date'>
                  <div className='tableHeaderWithArrow dueDate'><div>Analyst Deadline&nbsp;&nbsp;</div>
                  { sortHeader==='dueDate'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('clientDeadline') &&
                <th id='clientDeadline' onClick={handleTableHeaderClick} className='clientDeadline' title='Click to sort by due date'>
                  <div className='tableHeaderWithArrow clientDeadline'><div>Client Deadline&nbsp;&nbsp;</div>
                  { sortHeader==='clientDeadline'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('managerName') &&
                <th id='managerName' onClick={handleTableHeaderClick} title='Click to sort by Manager Name'>
                  <div className='tableHeaderWithArrow'><div>Manager&nbsp;&nbsp;</div>
                  { sortHeader==='managerName'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('assignedAnalystName') &&
                <th id='assignedAnalystName' onClick={handleTableHeaderClick} title='Click to sort by analyst assigned'>
                  <div className='tableHeaderWithArrow'><div>Analyst&nbsp;&nbsp;</div>
                  { sortHeader==='assignedAnalystName'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('ndaStatus') &&
                <th id='ndaStatus'>
                  <div>NDA Status</div>
                </th>
                }
                { !projectPreferences.includes('contractStatus') &&
                <th id='contractStatus'>
                  <div>Contract Status</div>
                </th>
                }
                { !projectPreferences.includes('firstInvoiceStatus') &&
                <th id='firstInvoiceStatus'>
                  <div>Invoice 1 Status</div>
                </th>
                }
                { !projectPreferences.includes('intakeFormStatus') &&
                <th id='intakeFormStatus' onClick={handleTableHeaderClick} title='Click to sort by intake form status'>
                  <div className='tableHeaderWithArrow'><div>Form Status&nbsp;&nbsp;</div>
                  { sortHeader==='intakeFormStatus'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('projectStatus') &&
                <th id='projectStatus' onClick={handleTableHeaderClick} title='Click to sort by project status'>
                  <div className='tableHeaderWithArrow'><div>Project Status&nbsp;&nbsp;</div>
                  { sortHeader==='projectStatus'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('projectReportStatus') &&
                <th id='projectReportStatus' onClick={handleTableHeaderClick} title='Click to sort by report status'>
                  <div className='tableHeaderWithArrow'><div>Report Status&nbsp;&nbsp;</div>
                  { sortHeader==='projectReportStatus'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('secondInvoiceStatus') &&
                <th id='secondInvoiceStatus'>
                  <div>Invoice 2 Status</div>
                </th>
                }
                { !projectPreferences.includes('ndaURL') &&
                <th id='ndaURL'>
                  <div>NDA Link</div>
                </th>
                }
                { !projectPreferences.includes('contractURL') &&
                <th id='contractURL'>
                  <div>Contract Link</div>
                </th>
                }
                { !projectPreferences.includes('firstInvoiceURL') &&
                <th id='firstInvoiceLink'>
                  <div>Invoice 1 Link</div>
                </th>
                }
                { !projectPreferences.includes('formURL') &&
                <th id='formLink'>
                  <div>Form Link</div>
                </th>
                }
                { !projectPreferences.includes('projectReportURL') &&
                <th id='projectReportLink'>
                  <div>Report Link</div>
                </th>
                }
                { !projectPreferences.includes('secondInvoiceURL') &&
                <th id='secondInvoiceLink'>
                  <div>Invoice 2 Link</div>
                </th>
                }
                { !projectPreferences.includes('otherURLs') &&
                <th id='otherLinks'>
                  <div>Other Links</div>
                </th>
                }
                { !projectPreferences.includes('createdAt') &&
                <th id='createdAt' onClick={handleTableHeaderClick} title='Click to sort by creation'>
                  <div className='tableHeaderWithArrow createdAt'><div>Creation&nbsp;&nbsp;</div>
                  { sortHeader==='createdAt'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                { !projectPreferences.includes('updatedAt') &&
                <th id='updatedAt' onClick={handleTableHeaderClick} title='Click to sort by creation'>
                  <div className='tableHeaderWithArrow updatedAt'><div>Last Update&nbsp;&nbsp;</div>
                  { sortHeader==='updatedAt'?
                      <>
                      {sortOrder==='asc' ?
                      <div>
                          <div className='sortIndicatorActive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                      : 
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorActive'>&#9660;</div>
                      </div>
                      }
                      </>
                      :
                      <div>
                          <div className='sortIndicatorInactive'>&#9650;</div>
                          <div className='sortIndicatorInactive'>&#9660;</div>
                      </div>
                  }
                  </div>
                </th>
                }
                <th id='actions' className='actions'>Actions</th>
              </tr>
            </thead>
            <tbody>
              { projects.length === 0 ?
              <tr>
                <td colSpan='12' className='noProjects text-center'>No Projects Found</td>
              </tr>
              :
              <>
              {projects.map(project => (
                <ProjectsItem user={user} key={project._id} projectItem={project} isMobile={isMobile} getProjects={getProjectsTimeline} toggleNotifications={toggleNotifications} projectPreferences={projectPreferences} services={services} />
              ))}
              </>
              }
            </tbody>
            </>
          }
          </table>
          <div>
            { projects.map(project => (
              <UpdateProject key={project._id} user={user} users={users} updateProjects={updateProjects} projectItem={project} toggleNotifications={toggleNotifications} />
            ))}
          </div>
        </div>
        <div className='footer'>
          <div>
            page count
          </div>
          <div>
            toggle
          </div>
        </div>
      </div>
      }
    </div>
  )
}
